import {useCallback, useEffect, useRef, useState} from 'react';
import {AiAssistantRequest, HibuAssistantMessage} from './types';
import {fetchStreamedAiResponse} from './utils';
import {useStreamedAnswer} from './useStreamedAnswer';

type ResponseCallbacks = {
    onComplete: (message: HibuAssistantMessage) => unknown;
    onError: (error: unknown) => unknown;
};

/** Share request lifecycle, display batching, and first-token scrolling across chat views. */
export function useAiChatStream() {
    const {text, appendToken, flush, reset} = useStreamedAnswer();
    const [isGeneratingAnswer, setIsGeneratingAnswer] = useState(false);
    const chatWindowRef = useRef<HTMLDivElement | null>(null);
    // Only callbacks from the latest request may change the displayed answer.
    const activeRequestId = useRef(0);

    useEffect(() => () => {activeRequestId.current++;}, []);

    const cancel = useCallback(() => {
        activeRequestId.current++;
        reset();
        setIsGeneratingAnswer(false);
    }, [reset]);

    const generate = useCallback(async (request: AiAssistantRequest, callbacks: ResponseCallbacks) => {
        const requestId = ++activeRequestId.current;
        const isCurrentRequest = () => requestId === activeRequestId.current;
        reset();
        setIsGeneratingAnswer(true);
        try {
            const message = await fetchStreamedAiResponse(request, {
                onFirstToken: () => {
                    if (isCurrentRequest()) chatWindowRef.current?.scrollIntoView({behavior: 'smooth', block: 'end'});
                },
                onToken: token => {
                    if (isCurrentRequest()) appendToken(token);
                },
            });
            if (!isCurrentRequest()) return;
            flush();
            await callbacks.onComplete(message);
        } catch (error) {
            if (!isCurrentRequest()) return;
            console.error('Error in fetchStreamedAiResponse:', error);
            callbacks.onError(error);
        } finally {
            if (isCurrentRequest()) {
                reset();
                setIsGeneratingAnswer(false);
            }
        }
    }, [appendToken, flush, reset]);

    return {text, isGeneratingAnswer, chatWindowRef, generate, cancel};
}
